/**
 * sqlite3mt control-plane coordination helpers
 *
 * Mechanical extraction from §96 §11.1–§11.2.
 *
 * This module owns the shared SAB wait/notify contract for runtime readiness,
 * commit gating, commit ACK publication, and fatal wakeups.
 */

import {
    DEFAULT_PAGE_SIZE,
    NUM_SLOTS,
    Sab4Scalar,
    SlotMetaI32,
    sab1SlotMetaView,
    sab4CommitAckRcView,
    sab4CommitAckSeqView,
    sab4CommitPostSeqView,
    sab4RuntimeEpochView,
    sab4ScalarView,
} from './sab-layout'
import {
    publishCheckpointGateClosed,
    publishWriteGateClosed,
} from './bootstrap'

export interface DeferredLike<T = void> {
    resolve(value?: T | PromiseLike<T>): void
}

export interface FatalWakeState {
    headAdvancePrepareWaiter: DeferredLike<void> | null
    commitRegistrationDrainWaiter: DeferredLike<void> | null
}

export interface CommitGatePermit {
    kind: 'PERMIT'
    writeSeq: number
    ckptSeq: number
}

export interface FatalWaitResult {
    kind: 'FATAL'
}

export interface CommitAckedResult {
    kind: 'ACKED'
    rc: CommitAckRc
}

export type CommitGateWaitResult = CommitGatePermit | FatalWaitResult
export type CommitAckWaitResult = CommitAckedResult | FatalWaitResult

export const COMMIT_ACK_RC_OK = 0
export const COMMIT_ACK_RC_FATAL = 1

export type CommitAckRc = typeof COMMIT_ACK_RC_OK | typeof COMMIT_ACK_RC_FATAL

export function abortIfFatal(runtime: { syncFatalError: unknown | null }): void {
    if (runtime.syncFatalError != null) {
        throw runtime.syncFatalError
    }
}

export function publishFatalFlagToSab(sab4: SharedArrayBuffer): void {
    Atomics.store(sab4ScalarView(sab4), Sab4Scalar.FATAL_FLAG, 1)
}

export function runtimeEpochMatches(
    sab4: SharedArrayBuffer,
    attachedRuntimeEpoch: bigint
): boolean {
    return Atomics.load(sab4RuntimeEpochView(sab4), 0) === attachedRuntimeEpoch
}

export function waitUntilControlReadyOrFatal(
    sab4: SharedArrayBuffer,
    attachedRuntimeEpoch: bigint
): 'READY' | 'FATAL' {
    const epochView = sab4RuntimeEpochView(sab4)
    const scalar = sab4ScalarView(sab4)

    for (;;) {
        if (Atomics.load(epochView, 0) !== attachedRuntimeEpoch) {
            return 'FATAL'
        }
        if (Atomics.load(scalar, Sab4Scalar.FATAL_FLAG) !== 0) {
            return 'FATAL'
        }
        if (Atomics.load(scalar, Sab4Scalar.CONTROL_READY) !== 0) {
            return 'READY'
        }

        const waitSeq = Atomics.load(scalar, Sab4Scalar.CONTROL_READY_WAITSEQ)
        if (Atomics.load(epochView, 0) !== attachedRuntimeEpoch) {
            return 'FATAL'
        }
        if (Atomics.load(scalar, Sab4Scalar.FATAL_FLAG) !== 0) {
            return 'FATAL'
        }
        if (Atomics.load(scalar, Sab4Scalar.CONTROL_READY) !== 0) {
            return 'READY'
        }

        Atomics.wait(scalar, Sab4Scalar.CONTROL_READY_WAITSEQ, waitSeq)
    }
}

export function waitUntilCommitAllowedAndCheckpointOpen(
    sab4: SharedArrayBuffer,
    attachedRuntimeEpoch: bigint
): CommitGateWaitResult {
    const scalar = sab4ScalarView(sab4)

    for (;;) {
        if (waitUntilControlReadyOrFatal(sab4, attachedRuntimeEpoch) === 'FATAL') {
            return { kind: 'FATAL' }
        }

        if (Atomics.load(scalar, Sab4Scalar.FATAL_FLAG) !== 0) {
            return { kind: 'FATAL' }
        }

        if (Atomics.load(scalar, Sab4Scalar.WRITE_GATE_CLOSED) !== 0) {
            return { kind: 'FATAL' }
        }

        const writeSeq = Atomics.load(scalar, Sab4Scalar.WRITE_GATE_WAITSEQ)
        const ckptSeq = Atomics.load(scalar, Sab4Scalar.CHECKPOINT_GATE_WAITSEQ)

        if (Atomics.load(scalar, Sab4Scalar.CHECKPOINT_GATE_CLOSED) !== 0) {
            Atomics.wait(scalar, Sab4Scalar.CHECKPOINT_GATE_WAITSEQ, ckptSeq)
            continue
        }

        if (Atomics.load(scalar, Sab4Scalar.FATAL_FLAG) !== 0) {
            return { kind: 'FATAL' }
        }

        if (Atomics.load(scalar, Sab4Scalar.WRITE_GATE_CLOSED) !== 0) {
            return { kind: 'FATAL' }
        }

        if (Atomics.load(scalar, Sab4Scalar.CHECKPOINT_GATE_CLOSED) !== 0) {
            continue
        }

        return {
            kind: 'PERMIT',
            writeSeq,
            ckptSeq,
        }
    }
}

export function publishCommitPosted(
    sab4: SharedArrayBuffer,
    connId: number,
    reqId: bigint
): void {
    validateConnId(sab4, connId)
    validateReqId(reqId)

    const postSeq = sab4CommitPostSeqView(sab4)
    const ackSeq = sab4CommitAckSeqView(sab4)
    const currentPost = Atomics.load(postSeq, connId)
    const currentAck = Atomics.load(ackSeq, connId)

    if (currentPost > currentAck) {
        throw new RangeError(`connection ${connId} already has an outstanding commit post`)
    }
    if (reqId <= currentPost) {
        throw new RangeError(`commit reqId must increase monotonically for connection ${connId}`)
    }

    Atomics.store(postSeq, connId, reqId)
}

export function publishCommitAck(
    sab4: SharedArrayBuffer,
    connId: number,
    reqId: bigint,
    rc: CommitAckRc
): void {
    validateConnId(sab4, connId)
    validateReqId(reqId)
    validateCommitAckRc(rc)

    const postSeq = sab4CommitPostSeqView(sab4)
    const ackSeq = sab4CommitAckSeqView(sab4)
    const ackRc = sab4CommitAckRcView(sab4)
    const currentPost = Atomics.load(postSeq, connId)
    const currentAck = Atomics.load(ackSeq, connId)

    if (reqId < currentAck) {
        throw new RangeError(`commit ACK reqId cannot move backwards for connection ${connId}`)
    }
    if (reqId > currentPost) {
        throw new RangeError(`cannot ACK unposted reqId ${reqId.toString()} for connection ${connId}`)
    }

    Atomics.store(ackRc, connId, rc)
    Atomics.store(ackSeq, connId, reqId)
    Atomics.notify(ackSeq, connId)
}

export function hasOutstandingCommitPost(
    sab4: SharedArrayBuffer,
    connId: number
): boolean {
    validateConnId(sab4, connId)

    const postSeq = sab4CommitPostSeqView(sab4)
    const ackSeq = sab4CommitAckSeqView(sab4)
    return Atomics.load(postSeq, connId) > Atomics.load(ackSeq, connId)
}

export function hasAnyOutstandingCommitPosts(sab4: SharedArrayBuffer): boolean {
    const postSeq = sab4CommitPostSeqView(sab4)
    const ackSeq = sab4CommitAckSeqView(sab4)

    for (let connId = 0; connId < postSeq.length; connId += 1) {
        if (Atomics.load(postSeq, connId) > Atomics.load(ackSeq, connId)) {
            return true
        }
    }

    return false
}

export function waitForCommitAckOrFatal(
    sab4: SharedArrayBuffer,
    attachedRuntimeEpoch: bigint,
    connId: number,
    reqId: bigint
): CommitAckWaitResult {
    validateConnId(sab4, connId)
    validateReqId(reqId)

    const epochView = sab4RuntimeEpochView(sab4)
    const scalar = sab4ScalarView(sab4)
    const ackSeq = sab4CommitAckSeqView(sab4)
    const ackRc = sab4CommitAckRcView(sab4)

    while (Atomics.load(ackSeq, connId) < reqId) {
        if (Atomics.load(scalar, Sab4Scalar.FATAL_FLAG) !== 0
            || Atomics.load(epochView, 0) !== attachedRuntimeEpoch) {
            return { kind: 'FATAL' }
        }

        const seen = Atomics.load(ackSeq, connId)
        Atomics.wait(ackSeq, connId, seen)
    }

    if ((Atomics.load(scalar, Sab4Scalar.FATAL_FLAG) !== 0
         || Atomics.load(epochView, 0) !== attachedRuntimeEpoch)
        && Atomics.load(ackSeq, connId) < reqId) {
        return { kind: 'FATAL' }
    }

    const rc = Atomics.load(ackRc, connId)
    validateCommitAckRc(rc)

    return {
        kind: 'ACKED',
        rc,
    }
}

export function wakeAllSlotWaiters(
    sab1: SharedArrayBuffer,
    pageSize: number = DEFAULT_PAGE_SIZE
): void {
    for (let slot = 0; slot < NUM_SLOTS; slot += 1) {
        const meta = sab1SlotMetaView(sab1, slot, pageSize)
        Atomics.add(meta, SlotMetaI32.WAITSEQ, 1)
        Atomics.notify(meta, SlotMetaI32.WAITSEQ)
    }
}

export function wakeFatalWaiters(
    sab1: SharedArrayBuffer,
    sab4: SharedArrayBuffer,
    waiters: FatalWakeState,
    pageSize: number = DEFAULT_PAGE_SIZE
): void {
    const scalar = sab4ScalarView(sab4)
    const ackSeq = sab4CommitAckSeqView(sab4)

    bumpAndNotify(scalar, Sab4Scalar.FRONTIER_WAITSEQ)
    bumpAndNotify(scalar, Sab4Scalar.WRITE_GATE_WAITSEQ)
    bumpAndNotify(scalar, Sab4Scalar.CHECKPOINT_GATE_WAITSEQ)
    bumpAndNotify(scalar, Sab4Scalar.CONTROL_READY_WAITSEQ)

    for (let connId = 0; connId < ackSeq.length; connId += 1) {
        Atomics.notify(ackSeq, connId)
    }

    wakeAllSlotWaiters(sab1, pageSize)

    if (waiters.headAdvancePrepareWaiter != null) {
        waiters.headAdvancePrepareWaiter.resolve()
        waiters.headAdvancePrepareWaiter = null
    }

    if (waiters.commitRegistrationDrainWaiter != null) {
        waiters.commitRegistrationDrainWaiter.resolve()
        waiters.commitRegistrationDrainWaiter = null
    }
}

/**
 * Spec-shaped fatal transition from §96 R39 / §11.2.
 *
 * The first transition owns the fatal error, closes both gates, and wakes all
 * relevant waiters. Later calls are no-ops so fatal publication stays monotone.
 */
export function enterFatalRuntimeState(
    runtime: { syncFatalError: unknown | null },
    sab1: SharedArrayBuffer,
    sab4: SharedArrayBuffer,
    waiters: FatalWakeState,
    error: unknown,
    writeGateReason: number,
    pageSize: number = DEFAULT_PAGE_SIZE
): boolean {
    if (runtime.syncFatalError != null) {
        return false
    }

    runtime.syncFatalError = error
    publishFatalFlagToSab(sab4)
    publishWriteGateClosed(sab4, writeGateReason)
    publishCheckpointGateClosed(sab4)
    wakeFatalWaiters(sab1, sab4, waiters, pageSize)
    return true
}

function validateConnId(sab4: SharedArrayBuffer, connId: number): void {
    if (!Number.isInteger(connId) || connId < 0 || connId >= sab4CommitPostSeqView(sab4).length) {
        throw new RangeError(`invalid sqlite3mt connection id: ${connId}`)
    }
}

function validateReqId(reqId: bigint): void {
    if (reqId < 0n) {
        throw new RangeError(`commit reqId must be non-negative, got ${reqId.toString()}`)
    }
}

function validateCommitAckRc(rc: number): asserts rc is CommitAckRc {
    if (rc !== COMMIT_ACK_RC_OK && rc !== COMMIT_ACK_RC_FATAL) {
        throw new RangeError(`commit ACK rc must be ${COMMIT_ACK_RC_OK} (OK) or ${COMMIT_ACK_RC_FATAL} (FATAL), got ${rc}`)
    }
}

function bumpAndNotify(scalar: Int32Array, index: number): void {
    Atomics.add(scalar, index, 1)
    Atomics.notify(scalar, index)
}
